import { atom } from "jotai";

/**
 * HOW MUCH FIXED FOOTER EXISTS RIGHT NOW
 *
 * Two fixed footers of different kinds — `FixedFooter` (opaque bar at
 * `bottom-0`) and `FixedBottomBar` (floating bag at `bottom-4`) — and anything
 * floating above them needs the height of whoever is on screen.
 *
 * Before this, `FloatingFullscreenButton` used a hard `bottom-20` plus a
 * per-page `!bottom-4` when the bag was empty — fine for a page-mounted button,
 * not for one on the `Router`. REGISTER (not one number) because both footers
 * coexist; storing one number lets the second overwrite the first.
 */
export const bottomChromeSlotsAtom = atom<Record<string, number>>({});

/**
 * Height floating content must clear: the LARGEST mounted footer, not the sum.
 * They overlap on screen — summing would lift the button mid-page.
 */
export const bottomChromeHeightAtom = atom((get) => {
  const heights = Object.values(get(bottomChromeSlotsAtom));
  return heights.length ? Math.max(...heights) : 0;
});

/**
 * Space the support button occupies, for whoever floats above it.
 *
 * Carries SIDE with height: only same-corner floaters need to rise.
 */
export type SupportButtonFootprint = {
  side: "left" | "right";
  height: number;
};

export const supportButtonFootprintAtom = atom<SupportButtonFootprint | null>(
  null,
);

/**
 * Gap between a floating piece and what sits under it — fixed footer when
 * present, screen floor when not, support button when they share a corner.
 *
 * Lives here, not on the button: both floaters need the same number.
 */
export const FLOATING_CHROME_GAP = 16;

/**
 * Vertical extent (offset + height, px) each mounted `FloatingAction` stack takes above the bottom chrome.
 * Kept apart from the footer slots: floaters position themselves from those, so mixing them would feed back.
 */
export const floatingActionSlotsAtom = atom<Record<string, number>>({});

/** Bottom padding page content needs to clear the tallest footer AND the floating buttons riding above it. */
export const pageBottomClearanceAtom = atom((get) => {
  const floating = Object.values(get(floatingActionSlotsAtom));
  const tallestFloater = floating.length ? Math.max(...floating) : 0;
  const floaterClearance = tallestFloater > 0 ? tallestFloater + FLOATING_CHROME_GAP : 0;
  return get(bottomChromeHeightAtom) + floaterClearance + FLOATING_CHROME_GAP;
});
